const HEADER_MEASURE_FONT =
    '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

let _measureCanvas;

/**
 * @param {string} text
 * @returns {number}
 */
export function measureHeaderTextPx(text) {
    const s = String(text ?? "");
    if (typeof document === "undefined") {
        return Math.ceil(s.length * 7.5);
    }
    if (!_measureCanvas) _measureCanvas = document.createElement("canvas");
    const ctx = _measureCanvas.getContext("2d");
    ctx.font = HEADER_MEASURE_FONT;
    return Math.ceil(ctx.measureText(s).width);
}

/**
 * Minimum column width from header label + optional icons (info, sorter, filter).
 *
 * @param {string} label
 * @param {{ hasInfoIcon?: boolean, hasSorter?: boolean, hasFilterIcon?: boolean }} [options]
 * @returns {number}
 */
export function headerColumnWidth(label, options = {}) {
    const { hasInfoIcon = false, hasSorter = false, hasFilterIcon = false } = options;
    const textPx = measureHeaderTextPx(label);
    const iconPx = hasInfoIcon ? 28 : 0;
    const sortPx = hasSorter ? 28 : 0;
    const filterPx = hasFilterIcon ? 18 : 0;
    const thPaddingPx = 40;
    return Math.max(80, textPx + iconPx + sortPx + filterPx + thPaddingPx);
}

/**
 * @param {{ label?: string, title?: unknown, key?: string }} col
 * @returns {string}
 */
export function extractColumnLabel(col) {
    if (typeof col.label === "string" && col.label) return col.label;
    if (typeof col.title === "string") return col.title;
    return String(col.key ?? "");
}

/**
 * @param {{ title?: unknown, label?: string }} col
 * @returns {boolean}
 */
export function columnHasInfoIcon(col) {
    return typeof col.title !== "string" && typeof col.label === "string" && col.label.length > 0;
}

/**
 * @param {Array<{ key: string, width?: number, title?: unknown, label?: string, sorter?: boolean }>} columns
 * @param {Set<string>} filteredKeys
 */
export function ensureColumnHeaderWidths(columns, filteredKeys) {
    return columns.map((col) => {
        const label = extractColumnLabel(col);
        const minWidth = headerColumnWidth(label, {
            hasInfoIcon: columnHasInfoIcon(col),
            hasSorter: !!col.sorter,
            hasFilterIcon: filteredKeys.has(col.key),
        });
        const width = Math.max(col.width || 0, minWidth);
        const prevOnHeaderCell = col.onHeaderCell;

        return {
            ...col,
            width,
            onHeaderCell: (...args) => {
                const prev = typeof prevOnHeaderCell === "function" ? prevOnHeaderCell(...args) : {};
                return {
                    ...prev,
                    style: {
                        ...(prev.style || {}),
                        minWidth: width,
                        width,
                        whiteSpace: "nowrap",
                    },
                };
            },
        };
    });
}

export const DEFAULT_SELECTION_COLUMN_WIDTH = 48;

/**
 * Sum of column widths for Ant Design `scroll.x` (numeric value required for sticky tables).
 *
 * @param {Array<{ width?: number }>} columns
 * @param {{ includeSelection?: boolean, selectionWidth?: number, buffer?: number, fallbackColumnWidth?: number }} [options]
 * @returns {number}
 */
export function getTableScrollX(columns, options = {}) {
    const {
        includeSelection = false,
        selectionWidth = DEFAULT_SELECTION_COLUMN_WIDTH,
        buffer = 16,
        fallbackColumnWidth = 120,
    } = options;

    const columnsWidth = (columns || []).reduce((sum, col) => {
        const w = Number(col.width);
        return sum + (Number.isFinite(w) && w > 0 ? w : fallbackColumnWidth);
    }, 0);

    const selection = includeSelection ? selectionWidth : 0;
    return columnsWidth + selection + buffer;
}
